import type { RMDXishOpts as Opts, RDMDProps as Props } from '@readme/backend/iso';
import type { RMDXishProps } from '@readme/backend/iso/types/rdmd';
import type { RunOpts } from '@readme/markdown/dist/lib/run';
import type { ComponentType, ReactElement } from 'react';

import { run, compile, Components, mdxish, renderMdxish } from '@readme/markdown';
import * as Sentry from '@sentry/browser';
import { cloneElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useEnvInfo from '@core/hooks/useEnvInfo';
import rdmdComponentOverrides from '@core/utils/rdmdComponentOverrides';

import Dehydrated from './Dehydrated';
import ErrorBoundary from './ErrorBoundary';
import RenderErrorFallback from './RenderErrorFallback';
import './style.scss';
import useDarkModeAttribute from './useDarkModeAttribute';

const { TailwindStyle } = Components;

const overrides = Object.entries(rdmdComponentOverrides).reduce((acc, [key, value]) => {
  acc[key] = { default: value };
  return acc;
}, {});

/**
 * Type for module exports from run() or renderMdxish().
 * Both return objects with string keys mapping to React components.
 */
type ModuleExports = Record<string, ComponentType>;

export function exec(body: string, opts: Opts, excerpt = false) {
  const customBlocksByExport = { ...opts.components };
  const customBlocks: Record<string, ModuleExports> = {};

  // Render custom blocks to react components
  // Still use mdx renderer even for mdxish because it's just jsx code
  // so we can use the same logic for rendering
  Object.entries(opts.components || {}).forEach(([tag, source]) => {
    let mod: ModuleExports | undefined;

    if (opts.componentTypes?.[tag] === 'component') {
      const code = compile(source, { ...opts, components: {} });
      mod = run(code, { ...opts, components: overrides } as RunOpts) as ModuleExports;
    } else if (opts.componentTypes?.[tag] === 'content') {
      const tree = mdxish(source, { useTailwind: true, ...opts, variables: opts.variables });
      mod = renderMdxish(tree, { ...opts, components: overrides }) as ModuleExports;
    } else {
      // Default behavior: use run() for backward compatibility
      const code = compile(source, { ...opts, components: {} });
      mod = run(code, { ...opts, components: overrides } as RunOpts) as ModuleExports;
    }

    if (mod) {
      Object.keys(mod).forEach(subTag => {
        // Skip reserved exports and top-level component names (top-level always takes precedence)
        if (['toc', 'Toc', 'default', 'stylesheet'].includes(subTag)) return;
        if (subTag in (opts.components || {})) return;

        customBlocksByExport[subTag] = source;
        // Flatten sub-exports to top-level so they're recognized as components in mdxish
        if (!customBlocks[subTag]) {
          customBlocks[subTag] = {
            default: mod[subTag] as ComponentType,
          };
        }
      });
      customBlocks[tag] = mod;
    }
  });

  const components = { ...overrides, ...customBlocks };
  const { copyButtons, terms, theme, variables } = opts;

  // Note: components must come AFTER ...opts to ensure they take precedence
  // - components: compiled functions override opts.components (source strings)
  const mdxishTree = mdxish(body, { useTailwind: true, ...opts, components, safeMode: opts.safeMode, variables });

  // Only take the first paragraph when rendering excerpts
  const renderedTree = excerpt ? { ...mdxishTree, children: mdxishTree.children.slice(0, 1) } : mdxishTree;

  const module = renderMdxish(renderedTree, {
    components,
    copyButtons,
    terms,
    theme,
    variables,
  });

  return module;
}

/**
 * Either a successfully-compiled module or a parse-phase error. Returning the error
 * as a value (instead of throwing) is the only way to signal failure across the SSR
 * boundary
 */
type RMDXISHResult = { module: ReturnType<typeof exec> } | { error: Error };

function useRMDXISH(
  body: string = '',
  excerpt = false,
  optsParam: Opts,
  UNSAFE_SSR_HYDRATE?: boolean,
  DEFER_CLIENT_RENDER?: boolean,
): RMDXISHResult | null {
  const { isServer } = useEnvInfo();

  if (optsParam.format === 'md' && optsParam.components) {
    // delete components from opts when rendering plain markdown,
    // so we don't try to sanitize mdx within the custom blocks
    delete optsParam.components;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const opts = useMemo(() => optsParam, [JSON.stringify(optsParam)]);

  return useMemo(() => {
    if ((isServer && !UNSAFE_SSR_HYDRATE) || DEFER_CLIENT_RENDER) return null;
    try {
      return { module: exec(body, opts, excerpt) };
    } catch (e) {
      const error = e as Error;
      Sentry.captureException(error, { tags: { source: 'rmdxish-parse' } });
      return { error };
    }
  }, [body, excerpt, opts, isServer, UNSAFE_SSR_HYDRATE, DEFER_CLIENT_RENDER]);
}

export const TOC = ({ body, children, errorFallback, opts = {} }: Props & { errorFallback?: ReactElement }) => {
  const { t } = useTranslation();
  const result = useRMDXISH(
    (body || children) as string,
    false,
    { useTailwind: false, ...opts },
    true, // ensure the TOC is always server rendered
  );
  if (!result) return null;
  if ('error' in result) return errorFallback ?? null;
  return <result.module.Toc heading={t('tableOfContents')} />;
};

interface RMDXISHComponentProps extends RMDXishProps {
  /**
   * Element to render when the mdxish renderer throws. When omitted, a generic
   * "Unable to render content" panel is shown.
   */
  errorFallback?: ReactElement;
}

const RMDXISH = ({
  body,
  children = body,
  className,
  css,
  dehydrated,
  errorFallback,
  excerpt,
  opts = {},
  skipBaseClassName = false,
  Tag = 'div',
  darkModeDataAttribute = undefined,
  ...rest
}: RMDXISHComponentProps) => {
  const doc = children ?? '';
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const mountTimer = window.setTimeout(() => {
      setIsMounted(true);
    }, 0);

    return () => window.clearTimeout(mountTimer);
  }, []);

  // Hydrate against the exact SSR dehydrated HTML first, then upgrade to compiled MDXish after mount.
  const shouldDeferClientRender = !!dehydrated && !isMounted;
  const result = useRMDXISH(doc, excerpt, opts, false, shouldDeferClientRender);
  const classes = [
    skipBaseClassName !== true && 'markdown-body',
    className || '',
    opts.useTailwindRoot && 'readme-tailwind',
  ];
  const props = { ...rest, className: `rm-Markdown ${classes.filter(c => c).join(' ')}`, 'data-testid': 'RDMD' };

  const darkModeAttr = useDarkModeAttribute(darkModeDataAttribute);

  if (!result) {
    if (dehydrated) return <Dehydrated {...props} string={dehydrated} Tag={Tag} />;
    return '';
  }

  const errorElement = errorFallback ?? (
    <RenderErrorFallback body={doc} engine="mdxish" message="Page content is not renderable with the MDXish renderer" />
  );

  // Parse-phase failure (malformed mdxish syntax). Render the fallback directly
  // ErrorBoundary cannot catch this since the throw happened at render time on the server.
  if ('error' in result) {
    return cloneElement(errorElement, { error: result.error, ...errorElement.props });
  }

  const Content = result.module;

  return (
    <ErrorBoundary body={doc} errorComponent={errorElement}>
      <Tag {...props}>
        {/* @todo: figure out how to clean this up */}
        {!!css && <style suppressHydrationWarning>{css}</style>}

        {opts.format === 'md' ? (
          <Content.default />
        ) : (
          <TailwindStyle darkModeDataAttribute={darkModeAttr}>
            <Content.default />
          </TailwindStyle>
        )}
      </Tag>
    </ErrorBoundary>
  );
};

export default RMDXISH;
